/**
 * Formatting utilities for display in tables, cards, and detail views.
 * Phase 2 scaffold — implementations added as features are built.
 */

export function formatRelativeTime(iso: string): string {
  const ts = Date.parse(iso);
  if (!Number.isFinite(ts)) return iso;

  const diffSec = Math.round((ts - Date.now()) / 1000);
  const abs = Math.abs(diffSec);
  const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });

  if (abs < 60) return rtf.format(diffSec, 'second');
  const diffMin = Math.round(diffSec / 60);
  if (Math.abs(diffMin) < 60) return rtf.format(diffMin, 'minute');
  const diffHour = Math.round(diffMin / 60);
  if (Math.abs(diffHour) < 24) return rtf.format(diffHour, 'hour');
  const diffDay = Math.round(diffHour / 24);
  if (Math.abs(diffDay) < 30) return rtf.format(diffDay, 'day');
  const diffMonth = Math.round(diffDay / 30);
  if (Math.abs(diffMonth) < 12) return rtf.format(diffMonth, 'month');
  return rtf.format(Math.round(diffMonth / 12), 'year');
}

export function formatPhoneNumber(e164: string): string {
  // TODO: Implement E.164 display formatting
  return e164;
}

export function formatDurationMs(ms: number): string {
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  if (hours > 0) return `${hours}h ${minutes % 60}m`;
  if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
  return `${seconds}s`;
}

export function truncateId(id: string, length = 8): string {
  if (id.length <= length) return id;
  return `${id.slice(0, length)}…`;
}

/** Domain-aware display labels — does not change API enum values. */
const STATUS_DISPLAY: Record<string, string> = {
  // Salesforce Org: active → Connected
  connected: 'Connected',
  disconnected: 'Disconnected',
  // Sync
  synced: 'Synced',
  not_synced: 'Not Synced',
  // Presence
  online: 'Online',
  offline: 'Offline',
  away: 'Away',
  unknown: 'Unknown',
  // Environment
  production: 'Production',
  sandbox: 'Sandbox',
  // Agent account
  invited: 'Invited',
  disabled: 'Disabled',
  // Organization / connection
  active: 'Active',
  inactive: 'Inactive',
  suspended: 'Suspended',
  archived: 'Archived',
  error: 'Error',
};

export function formatStatusLabel(status: string, entity?: string): string {
  const key = status.toLowerCase();
  if (entity === 'salesforce_org' && key === 'active') return 'Connected';
  if (STATUS_DISPLAY[key]) return STATUS_DISPLAY[key];
  return status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}
